import { Container } from '../odie';
import { waitAFrame } from '../lib/util/waitAFrame';
import { app } from './app';
import { PuzzleMapImporter } from './util/PuzzleMapImporter';
import { GameScene } from './GameScene';
import { RescueLevelSystem } from './systems/RescueLevelSystem';
import type { BlockEntity } from './entities/BlockEntity';

export interface Options {
  stage: Container;
  levelData: string;
  infiniteMoves?: boolean;
  onObstacleCleared?: (entity: BlockEntity) => void;
}

export class CustomGameScene extends GameScene {
  private levelData: string;
  private infiniteMoves: boolean;
  private rescueSystem: RescueLevelSystem;

  constructor(options: Options) {
    super({ stage: options.stage });

    this.rescueSystem = this.addSystem(RescueLevelSystem);
    this.rescueSystem.onObstacleCleared = options.onObstacleCleared;

    this.levelData = options.levelData;
    this.infiniteMoves = options.infiniteMoves || false;
  }

  protected updateMoves() {
    if (this.infiniteMoves) return;
    if (this.moves > 0) this.moves -= 1;
  }

  public async startGame() {
    // Hide score progress and skip victory animation
    this.scoreSystem.setInvisible(true);

    this.completePromise = new Promise((resolve) => {
      this.completePromiseResolve = resolve;
    });

    // Start game's internal app ticker
    app.start();

    this.level = 0;
    this.gameOver = false;
    this.levelBroken = false;
    this.continues = 0;
    this.extraMoves = 0;
    this.socialContinueMoves = 0;
    this.adContinueMoves = 0;
    this.usedAutoShuffles = 0;
    this.usedBoosters = {};
    this.blocksBroken = {};
    this.powerBlocksCreated = {};
    this.blocksBrokenAnalytics = {};

    const mapDef = new PuzzleMapImporter(this.levelData).import();
    if (!mapDef) throw new Error('Invalid map def');

    // set map
    this.mapDef = {
      ...mapDef,
      levelVersion: 'custom',
      regionVersion: 'custom',
      levelHash: 'custom',
      regionHash: 'custom',
    };

    this.moves = this.mapDef.moves;

    this.sessionSystem.setMap(this.mapDef);

    // start
    super.start();

    // TODO: shouldn't need this. but apparently pixi issue.
    // wait 3 frames to allow all systems to have started and stepped first frame
    await waitAFrame(3);

    // set active
    this._active = true;

    this.onGameStart();
  }

  public finish() {
    // Fully disable gameplay interaction on finish
    this.stage.view.updateOpts({
      blockEvents: true,
    });

    // Dispatch a finish event, so any UI from outside the game can be updated too
    this.events.publish({ id: 'finish' });
  }

  public resolveCompletePromise() {
    if (this.completePromiseResolve) {
      this.completePromiseResolve();
      this.completePromiseResolve = undefined;
    }
  }

  public fail() {
    this.moves = 0;
    this.gameOver = true;
  }
}
